import { makeAutoObservable } from 'mobx';
import {ApiAuthEmailService, ApiUsersService} from "../../../shared/api";
import {clearTokens, isImpersonating, setTokens} from "../../../shared/lib/session";

const rank = (level) => (level === 'edit' ? 2 : level === 'view' ? 1 : 0);

// The panel is for users whose roles open at least one of its sections (admin «Роли»). What they may open and change
// comes from the API (/users/me/access) after login and at every start.
class AuthModel {
  isAuth = false;
  // { isAdmin, staff, sections: { users: 'view' | 'edit', ... }, portal, fields } or null
  access = null;

  constructor() {
    makeAutoObservable(this);
  }

  get isAdmin() {
    return Boolean(this.access && this.access.isAdmin);
  }

  // «Смотреть как» in this tab: { user: {id, name}, by: {id, name} } or null
  get impersonation() {
    return (this.access && this.access.impersonation) || null;
  }

  // may open a section of the panel / change things there
  canView(section) {
    return Boolean(this.access) && (this.access.isAdmin || rank(this.access.sections[section]) >= 1);
  }

  // never in a «Смотреть как» session: it only reads
  canEdit(section) {
    if (this.impersonation) return false;
    return Boolean(this.access) && (this.access.isAdmin || rank(this.access.sections[section]) >= 2);
  }

  setAuth(isAuth) {
    this.isAuth = isAuth;
  }

  setAccess(access) {
    this.access = access;
  }

  // the rights of the saved session: 'ok' (may use the panel), 'denied' (no sections), 'error' (no answer)
  async loadAccess() {
    try {
      const {data} = await ApiUsersService.fetchAccess();
      this.setAccess(data);
      return data && (data.isAdmin || data.staff) ? 'ok' : 'denied';
    } catch (err) {
      this.setAccess(null);
      return 'error';
    }
  }

  async login(login, password) {
    try {
      const response = await ApiAuthEmailService.loginByEmail(login, password);
      const { accessToken, refreshToken } = response.data;
      // one's own session: a «Смотреть как» one of this tab ends first
      if (isImpersonating()) clearTokens();
      setTokens(accessToken, refreshToken);
    } catch(err) {
      const blocked = err?.response?.status === 403 && err.response.data?.errors?.message;
      alert(blocked || 'Неверный логин или пароль');
      return;
    }
    const access = await this.loadAccess();
    if (access !== 'ok') {
      this.logout();
      alert(access === 'denied'
        ? 'Нет доступа к панели: у ваших ролей нет её разделов. Обратитесь к администратору.'
        : 'Сервер не ответил, попробуйте ещё раз');
      return;
    }
    this.setAuth(true);
  }

  // ends this tab's session; in a «Смотреть как» tab only that one (the administrator's own stays)
  logout() {
    clearTokens();
    this.setAccess(null);
    this.setAuth(false);
  }

}

export const authModel = new AuthModel();
